import crypto from 'crypto-browserify';
import { ethers, Wallet } from 'ethers';
// Resources
import { getLegacySalts, getMemberSalts } from '@/resources/wallet-service.resource';
import { crypto as legacyCrypto } from './legacyDecrypt';
export const ERC20_ABI = [
  {
    constant: true,
    inputs: [{ name: '_owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: 'balance', type: 'uint256' }],
    type: 'function',
  },
  {
    constant: true,
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    name: 'allowance',
    outputs: [{ name: '', type: 'uint256' }],
    type: 'function',
  },
  {
    constant: false,
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    type: 'function',
  },
  {
    constant: true,
    inputs: [],
    name: 'decimals',
    outputs: [{ name: '', type: 'uint8' }],
    type: 'function',
  },
];

// -----Getting and Decrypting mnemonic for signing transactions-----
export async function getEncryptedData(fetcher) {
  // get encrypted data from api
  return await fetcher(getMemberSalts())
    .then((data) => {
      return data;
    })
    .catch((err) => {
      console.error(err);
    });
}

export function createAesInstance(key, iv) {
  const keySize = 256;
  const blockSize = 128;
  const iterations = 1000;

  const derived = crypto.pbkdf2Sync(
    key,
    iv,
    iterations,
    (keySize + blockSize) / 8,
    'sha1'
  );

  return {
    key: derived.slice(0, keySize / 8),
    iv: derived.slice(keySize / 8),
  };
}

export function decryptMnemonic(encryptedData, passcode) {
  const cipher = encryptedData.encryptedMnemonic;
  const iv = encryptedData.salt64;

  try {
    const cipherBytes = Buffer.from(cipher, 'base64');
    const keyBytes = crypto
      .createHash('sha256')
      .update(Buffer.from(passcode, 'utf8'))
      .digest();
    const ivBytes = Buffer.from(iv, 'utf8');

    const { key: derivedKey, iv: derivedIV } = createAesInstance(keyBytes, ivBytes);

    const decipher = crypto.createDecipheriv('aes-256-cbc', derivedKey, derivedIV);
    let decrypted = decipher.update(cipherBytes);
    decrypted = Buffer.concat([decrypted, decipher.final()]);

    // Trim zero padding
    let lastZeroIndex = decrypted.length;
    for (let i = decrypted.length - 1; i >= 0; i--) {
      if (decrypted[i] === 0) {
        lastZeroIndex = i;
      } else {
        break;
      }
    }
    decrypted = decrypted.slice(0, lastZeroIndex);
    return decrypted.toString('utf8');
  } catch (err) {
    console.error(err);
    const error = new Error(
      'Decryption failed, please verify the passcode and try again.'
    );
    error.ErrorCode = 70001;
    throw error;
  }
}

export function calculateTokenAmount(amount, decimals) {
  try {
    const result = ethers.utils.parseUnits(amount.toString(), decimals);
    return result;
  } catch (err) {
    console.error('Error calculating token amount:', {
      error: err.message,
      input: amount,
      decimals,
    });
    throw err;
  }
}

export function formatTokenAmount(amount, decimals) {
  try {
    const result = ethers.utils.formatUnits(amount, decimals);
    return result;
  } catch (err) {
    console.error('Error formatting token amount:', {
      error: err.message,
      input: amount.toString(),
      decimals,
    });
    throw err;
  }
}

export async function prepareWallet(fetcher, passcode, rpcProvider) {
  const encryptedData = await getEncryptedData(fetcher);
  if (!encryptedData) throw new Error('No encrypted data available');

  const mnemonic = decryptMnemonic(encryptedData, passcode);
  const wallet = Wallet.fromMnemonic(mnemonic).connect(rpcProvider);
  return wallet;
}

export async function getTokenQuote(fromToken, toToken, amount, provider) {
  try {
    // Create contract instances
    const tokenOutContract = new ethers.Contract(toToken.address, ERC20_ABI, provider);

    try {
      // Get balance using v5 syntax
      const balance = await tokenOutContract.balanceOf(provider.address);

      const amountIn = ethers.utils.parseUnits(amount.toString(), fromToken.decimals);

      const formattedQuote = ethers.utils.formatUnits(balance, toToken.decimals);

      return {
        amountIn,
        amountOut: balance,
        formattedQuote,
      };
    } catch (err) {
      console.error('Error getting token balance:', {
        error: err.message,
        code: err.code,
        tokenAddress: toToken.address,
        providerAddress: provider.address,
      });
      throw err;
    }
  } catch (err) {
    console.error('Fatal error in getTokenQuote:', {
      error: err.message,
      code: err.code,
      stack: err.stack,
    });
    throw err;
  }
}

export async function decryptLegacyMnemonic(from, passcode, fetcher) {
  try {
    const encryptedData = await getLegacyEncryptedData(from, fetcher);
    if (!encryptedData) {
      throw new Error('No encrypted data available');
    }

    // Use the frontend legacy crypto
    const result = legacyCrypto.decrypt(encryptedData, passcode);
    if (result && result.decryptedString) {
      return result.decryptedString;
    } else {
      // This should throw the specific error from legacyDecrypt
      throw new Error(legacyCrypto.ERROR_INCORRECT_SECRET);
    }
  } catch (error) {
    console.error('Error decrypting legacy mnemonic:', error);

    // Check if this is the specific incorrect password error
    if (error.message === legacyCrypto.ERROR_INCORRECT_SECRET) {
      const authError = new Error('Incorrect passcode. Please try again.');
      authError.ErrorCode = 70001;
      throw authError;
    }

    // For other errors, preserve the original error but add the error code
    error.ErrorCode = 70001;
    throw error;
  }
}

export async function getLegacyEncryptedData(from, fetcher) {
  // get encrypted data from api
  return await fetcher(getLegacySalts(from))
    .then((data) => {
      return data;
    })
    .catch((err) => {
      console.error(err);
    });
}
